import { useMemo, memo } from "react";
import type { ToolUIPart } from "../../shared/types";
import { CardShell } from "./shared/CardShell";
import { InlineToolPanel } from "../InlineToolPanel";
import { parseToolOutput, baseToolName } from "./shared/utils";

interface SheetActionResult {
  ok: true;
  // sheets_write
  updatedRange?: string;
  updatedRows?: number;
  updatedColumns?: number;
  updatedCells?: number;
  // sheets_append
  tableRange?: string;
  updates?: { updatedRows?: number; updatedCells?: number };
  // sheets_create
  spreadsheetId?: string;
  spreadsheetTitle?: string;
  title?: string;
  webViewLink?: string;
  url?: string;
  // sheets_add_tab
  sheetTitle?: string;
  sheetId?: number;
  message?: string;
}

interface SheetActionCardProps {
  part: ToolUIPart;
}

export const SheetActionCard = memo(function SheetActionCard({ part }: SheetActionCardProps) {
  const isStreaming = part.state === "input-streaming";
  const tool = baseToolName(part.toolName);

  const result = useMemo(
    () => parseToolOutput<SheetActionResult>(part.state === "output-available" ? part.output : undefined),
    [part.state, part.output],
  );

  if (part.state === "output-available" && result.kind === "parse-failed") {
    return <InlineToolPanel part={part} />;
  }

  const isError = result.kind === "error";
  const data = result.kind === "ok" ? result.data : null;

  function getHeadline(): string {
    if (isStreaming) return "Working...";
    if (isError) return "Sheets Error";
    if (!data) return "Done";
    switch (tool) {
      case "sheets_write": {
        const cells = data.updatedCells ?? 0;
        return cells > 0 ? `Wrote ${cells} cell${cells !== 1 ? "s" : ""}` : "Cells written";
      }
      case "sheets_append": {
        const rows = data.updates?.updatedRows ?? data.updatedRows ?? 0;
        return rows > 0 ? `Appended ${rows} row${rows !== 1 ? "s" : ""}` : "Rows appended";
      }
      case "sheets_create":
        return `Created spreadsheet`;
      case "sheets_add_tab":
        return data.sheetTitle ? `Added tab "${data.sheetTitle}"` : "Tab added";
      default:
        return "Sheets updated";
    }
  }

  function getDetail(): string | null {
    if (!data) return null;
    switch (tool) {
      case "sheets_write":
        return data.updatedRange ?? null;
      case "sheets_append":
        return data.tableRange ?? data.updatedRange ?? null;
      case "sheets_create":
        return data.spreadsheetTitle ?? data.title ?? null;
      case "sheets_add_tab":
        return data.message ?? null;
      default:
        return data.message ?? null;
    }
  }

  const link = data?.webViewLink ?? data?.url;
  const headline = getHeadline();
  const detail = getDetail();
  const status = isStreaming ? "streaming" : isError ? "error" : "ready";
  const accent = isError ? "error" : "sheets";

  return (
    <CardShell
      icon={isError ? "✗" : "⊞"}
      title={headline}
      accent={accent}
      status={status}
    >
      {isStreaming && (
        <div className="tool-card__status-body">
          <span className="tool-card__status-icon" aria-hidden="true">⋯</span>
          <div className="tool-card__status-text">Updating spreadsheet...</div>
        </div>
      )}
      {isError && (
        <div className="tool-card__status-body">
          <span className="tool-card__status-icon" aria-hidden="true">✗</span>
          <div>
            <div className="tool-card__status-text">Sheets operation failed</div>
            <div className="tool-card__status-detail">{result.kind === "error" ? result.error : "Error"}</div>
          </div>
        </div>
      )}
      {!isStreaming && !isError && (
        <div className="tool-card__status-body">
          <span className="tool-card__status-icon" aria-hidden="true">✓</span>
          <div>
            {detail && (
              <div className="tool-card__status-detail">{detail}</div>
            )}
            {link && (
              <a
                className="tool-card__doc-link-inline"
                href={link}
                target="_blank"
                rel="noreferrer noopener"
                aria-label={`Open ${data?.spreadsheetTitle ?? data?.title ?? "spreadsheet"} in Sheets (opens in new tab)`}
              >
                Open spreadsheet ↗
              </a>
            )}
          </div>
        </div>
      )}
    </CardShell>
  );
});
